import { apiClient } from "@/api/api-client";
import type { CreateProjectAttachmentRequest, ProjectAttachment, ProjectAttachmentsResponse, UpdateProjectAttachmentRequest } from "@/types/project-attachment";

const projectAttachmentService = {
	/**
	 * Get all project attachments with optional filtering
	 */
	async getAll(params?: {
		per_page?: number;
		page?: number;
		project_id?: string;
		type?: string;
		project_name?: string;
	}): Promise<ProjectAttachmentsResponse> {
		const response = await apiClient.get<ProjectAttachmentsResponse>("/project-attachments", { params });
		return response.data;
	},

	/**
	 * Get attachments for a specific project
	 */
	async getByProject(
		projectId: string,
		params?: {
			per_page?: number;
			page?: number;
			type?: string;
		},
	): Promise<ProjectAttachmentsResponse> {
		const response = await apiClient.get<ProjectAttachmentsResponse>(`/projects/${projectId}/attachments`, { params });
		return response.data;
	},

	/**
	 * Get attachments for a specific sales contract
	 */
	async getBySalesContract(
		salesContractId: string,
		params?: {
			per_page?: number;
			page?: number;
			type?: string;
		},
	): Promise<ProjectAttachmentsResponse> {
		const response = await apiClient.get<ProjectAttachmentsResponse>(`/sales-contracts/${salesContractId}/attachments`, { params });
		return response.data;
	},

	/**
	 * Get a single project attachment
	 */
	async get(id: string): Promise<ProjectAttachment> {
		const response = await apiClient.get<{ data: ProjectAttachment }>(`/project-attachments/${id}`);
		return response.data.data;
	},

	/**
	 * Upload a new project attachment
	 */
	async create(data: CreateProjectAttachmentRequest): Promise<ProjectAttachment> {
		const formData = new FormData();
		if (data.project_id) {
			formData.append("project_id", data.project_id);
		}
		if (data.sales_contract_id) {
			formData.append("sales_contract_id", data.sales_contract_id);
		}
		formData.append("type", data.type);
		formData.append("file", data.file);
		if (data.description) {
			formData.append("description", data.description);
		}
		if (data.document_date) {
			formData.append("document_date", data.document_date);
		}

		const response = await apiClient.post<{ data: ProjectAttachment }>("/project-attachments", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	/**
	 * Update a project attachment
	 */
	async update(id: string, data: UpdateProjectAttachmentRequest): Promise<ProjectAttachment> {
		const formData = new FormData();

		if (data.type) {
			formData.append("type", data.type);
		}
		if (data.file) {
			formData.append("file", data.file);
		}
		if (data.description !== undefined) {
			formData.append("description", data.description);
		}
		if (data.document_date !== undefined) {
			formData.append("document_date", data.document_date ?? "");
		}

		// Use POST with _method override for file uploads
		formData.append("_method", "PATCH");

		const response = await apiClient.post<{ data: ProjectAttachment }>(`/project-attachments/${id}`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},

	/**
	 * Delete a project attachment
	 */
	async delete(id: string): Promise<void> {
		await apiClient.delete(`/project-attachments/${id}`);
	},

	/**
	 * Download a project attachment
	 */
	async download(id: string): Promise<Blob> {
		const response = await apiClient.get(`/project-attachments/${id}/download`, {
			responseType: "blob",
		});
		return response.data;
	},

	/**
	 * Get the download URL for a project attachment
	 */
	getDownloadUrl(id: string): string {
		const baseUrl = apiClient.defaults.baseURL || "";
		return `${baseUrl}project-attachments/${id}/download`;
	},
};

export default projectAttachmentService;
